import {
  isPercentWithoutMax,
  resolveDefaultWeight,
  roundChainKg,
  type EffortIntent,
  type LastLoggedSet,
} from "./resolveDefaultWeight.js";
import type { WeightSource } from "./schemas/weightSource.js";
import type { Exercise } from "./schemas/exercise.js";
import { exerciseTraits } from "./exerciseTraits.js";
import type { AmrapStop, RoundType, SetWork } from "./schemas/program.js";
import type { PlannedExerciseEntry, PlannedSet } from "./schemas/plan.js";
import type { MaxForProposal } from "./resolveDefaultWeight.js";
import { rpeToRir } from "./effortScale.js";
import type { TodayProposalView } from "./todaySessionProposal.js";

// Bridge type between the plan-list surface ("Start" trigger) and the
// logger (form prefill consumer). The trigger side resolves percentage
// targets to kg against the user's live MAXes before firing
// pushPendingStart; the logger only has to render the prefilled values
// into its existing weighted-set form.
//
// fromPlanId is preserved through to the workout save payload so the
// API can flip the plan row to status=completed. For program-started
// workouts there's no plan to complete, so planId stays absent.
//
// Lifted from apps/web/src/plans/start.ts (removed with RW-14, readable at 7ae8e35e) so the mobile Plan tab can
// build the same starter shape from the same percent-of-MAX +
// bodyweight conversion. Pure TS, no React, no DOM.

export type PlanStarter = {
  planId?: string;
  /** Workout-level title carried into the logger draft so the saved
   *  session is identifiable in history without the user retyping it.
   *  120-char cap mirrors Workout.name; trigger sites truncate before
   *  firing. */
  name?: string;
  notes?: string;
  /** Read-only coach context for the logger's workout-level "?" panel.
   *  Resolved at Start from the program/plan template (program description +
   *  the day's session note, or a single plan's note). Display-only — NEVER
   *  flows into the save payload, so it can't become a denormalized snapshot
   *  on the saved session. Absent for ad-hoc/manual workouts. */
  coachNotes?: { programDescription?: string; sessionNote?: string };
  /** AP-9b: the reasons behind a built session, for the logger's draft card.
   *  Display-only like `coachNotes`: it never reaches the save payload. */
  todayProposal?: TodayProposalView;
  /** #336 S2: the program round's type, when not normal. Display-only like
   *  `coachNotes`: it never reaches the save payload. */
  roundType?: Exclude<RoundType, "normal">;
  entries: {
    exerciseId: string;
    notes?: string;
    /** Per-entry RPE-column default (Logger redesign slice 3). True
     *  means the Logger opens with the RPE column visible for this
     *  entry. Propagated from PlannedExerciseEntry.rpeEnabled (which
     *  in turn carries Slot.rpeEnabled via programResolution). */
    rpeEnabled?: boolean;
    /** Per-entry RIR-column default (Phase 8). Mirror of rpeEnabled.
     *  Propagated from PlannedExerciseEntry.rirEnabled. */
    rirEnabled?: boolean;
    /** #353: prescribed at a % of a MAX the athlete does not have, so the
     *  logger offers to calibrate one. Weighted exercises only: the MAX
     *  proposal reads weighted sets. Display only, never saved. */
    calibrate?: true;
    /** Forwarded to WorkoutEntry.group when the logger materialises
     *  this starter into a draft. ≥2 entries that share an id form
     *  a superset or circuit window. */
    group?: { id: string; kind: "superset" | "circuit" };
    sets: {
      reps: number;
      weightKg: number; // already-resolved canonical kg
      /** Provenance of the prefilled weight. Stamped by
       *  resolveDefaultWeight in buildPlanStarter. Omitted only when
       *  the chain returned `'blank'` (no prescription + no history) —
       *  the logger treats absence as "leave the cell empty". Forwarded
       *  onto DraftSet.weightSource on hydrate, then to LogSet.weightSource
       *  on save (data-only, no UI cue). */
      weightSource?: WeightSource;
      rpe?: number;
      /** RIR target (0..10) from a "rir"-kind program slot. Mirror of
       *  rpe; a planned set carries at most one. The logger pre-fills
       *  whichever matches the athlete's effortScale, converting the
       *  other via effortScale.ts at hydrate time. */
      rir?: number;
      /** AMRAP-on-last-set hint from the program resolver. Carried
       *  through so the logger can render "X+ reps" placeholder. */
      amrap?: boolean;
      /** #355: where that AMRAP stops. Display only, never persisted. */
      amrapStop?: AmrapStop;
      /** #336 S3: the rep range around `reps`, both or neither. Display
       *  only: the logger prefills `reps` and shows the range beside it. */
      repsMin?: number;
      repsMax?: number;
      /** TIME AXIS (HI-1 slice 2). The two fields PlannedSet has carried
       *  since slice 1, forwarded so they survive the starter boundary.
       *  ⚠️ They did NOT before: PlannedSet.work/restSec stopped here, and
       *  a 30/90 bout authored in the program editor reached the athlete as
       *  a bare reps set — the logger never reads a PlannedSet, it reads
       *  this. Display/execution only, never persisted: the RESULT of a
       *  bout is LogSet.duration, which the athlete's clock earns. */
      work?: SetWork;
      restSec?: number;
      /** Coach's per-set prescription cue (SetPrescription.notes, 200 cap):
       *  e.g. "last set AMRAP if technique is solid", "OHP reintroduced —
       *  RIR2-3 (shoulder)". Display-only, read-only; rendered under the set
       *  row. Not persisted — the athlete's own diary is the timeline note. */
      notes?: string;
    }[];
  }[];
};

export type StarterContext = {
  /** exerciseId → the athlete's current MAX, freshly fetched at "Start" time
   *  so a MAX update between draft and execution shifts the targets.
   *
   *  ⚡ Was `Map<string, number>` plus a parallel `maxSources` narrowed by
   *  `maxSourcesFrom`. That pair threw away `setAt`, which §17.5 now needs to
   *  decide whether a MAX may LEAD the proposal chain — and both callers had
   *  the whole `CurrentMax` in hand already (`useMaxesData.getFreshDetailed`)
   *  and were flattening it on the way in. One shape, nothing dropped: a
   *  narrowing helper is where the next field would have died too. */
  currentMaxes: Map<string, MaxForProposal>;
  /** bodyweight at "Start" time, null when the user hasn't set a
   *  value. Used to convert percent-of-MAX targets on bodyweight
   *  exercises into an "extra" delta (effective − bw). */
  bodyweightKg: number | null;
  /** exerciseId → the athlete's most recently logged weighted set (kg AND
   *  reps) on that exercise. Drives the history steps of the
   *  resolveDefaultWeight chain: a percent-of-MAX set with no recorded MAX,
   *  an explicit `kind: 'history'` set, and a bare `kind: 'none'` set all
   *  fall back to this map, and the EFFORT arm turns it into an e1RM — which
   *  is why the reps are here and not only the kg. Optional with default
   *  `new Map()` so existing callsites that don't yet plumb a history fetch
   *  keep compiling; those just get the `'blank'` fallback. Callers populate
   *  best-effort — empty entries are fine, the chain treats missing slugs as
   *  "no history". */
  lastLoggedSets?: Map<string, LastLoggedSet>;
  /** Read-only slice of the shared catalog: only the fields needed
   *  to detect isBodyweight per exercise. */
  catalogItems: Pick<Exercise, "slug" | "isBodyweight">[];
  /** The clock the MAX age gate reads (§17.5). Injectable so a test can pin
   *  it; every real caller omits it and gets `Date.now()`. */
  nowMs?: number;
};

// The effort target of a planned set, in the canonical RIR scale, or
// undefined when this set's load is not delegated to the app (PG-9).
//
// ⛔ Two refusals live here rather than inside the chain, both because they
// depend on things the chain deliberately does not know:
//   • BODYWEIGHT. A MAX recorded against a bodyweight exercise is the TOTAL
//     load while the logger shows the EXTRA, and the history anchor is
//     already an extra — so an e1RM built from either compares two different
//     quantities. `convertWeightByMaxRatio` refuses these for the identical
//     reason; this is the same refusal, made by the same caller that knows
//     the catalog row.
//   • A SET WITH NO REPS. "reps + RIR to failure" needs a rep count; a floor
//     of 0 (pure freestyle, an AMRAP with no floor) would read as "RIR 2 at
//     zero reps" and propose something close to a maximum.
const effortIntentForSet = (
  s: PlannedSet,
  isBodyweight: boolean,
): EffortIntent | undefined => {
  if (isBodyweight) return undefined;
  if (!Number.isFinite(s.targetReps) || s.targetReps < 1) return undefined;
  // RIR is the canonical unit; an RPE-authored target is the same datum in
  // the other scale, so it is converted rather than dropped (effortScale.ts).
  const targetRir =
    s.targetRir ?? (s.targetRpe !== undefined ? rpeToRir(s.targetRpe) : undefined);
  if (targetRir === undefined) return undefined;
  return { targetRir, reps: s.targetReps };
};

export const buildPlanStarter = (params: {
  planId?: string;
  name?: string;
  notes?: string;
  coachNotes?: { programDescription?: string; sessionNote?: string };
  todayProposal?: TodayProposalView;
  roundType?: PlanStarter["roundType"];
  entries: PlannedExerciseEntry[];
  ctx: StarterContext;
}): PlanStarter => {
  const { planId, name, notes, coachNotes, todayProposal, roundType, entries, ctx } =
    params;
  // Workout.name caps at 120 chars; truncate here so program-composed
  // titles never bounce off the schema validator at save time. We trim
  // trailing whitespace so a mid-word slice doesn't leave a dangling
  // separator.
  const trimmedName = name?.trim().slice(0, 120).trim();
  const hasCoachNotes =
    coachNotes !== undefined &&
    (coachNotes.programDescription !== undefined ||
      coachNotes.sessionNote !== undefined);
  return {
    ...(planId !== undefined ? { planId } : {}),
    ...(trimmedName ? { name: trimmedName } : {}),
    ...(notes ? { notes } : {}),
    ...(hasCoachNotes ? { coachNotes } : {}),
    ...(todayProposal ? { todayProposal } : {}),
    ...(roundType ? { roundType } : {}),
    entries: entries.map((entry) => {
      const ex = ctx.catalogItems.find((c) => c.slug === entry.exerciseId);
      const isBw = exerciseTraits(ex).isBodyweight;
      const currentMax = ctx.currentMaxes.get(entry.exerciseId);
      const calibrate =
        !isBw &&
        entry.sets.some((s) => isPercentWithoutMax(s.targetWeight.kind, currentMax?.valueKg));
      return {
        exerciseId: entry.exerciseId,
        ...(calibrate ? { calibrate: true as const } : {}),
        ...(entry.notes ? { notes: entry.notes } : {}),
        ...(entry.rpeEnabled === true ? { rpeEnabled: true } : {}),
        ...(entry.rirEnabled === true ? { rirEnabled: true } : {}),
        ...(entry.group
          ? {
              group: {
                id: entry.group.id,
                kind: entry.group.kind,
                ...(entry.group.name !== undefined
                  ? { name: entry.group.name }
                  : {}),
              },
            }
          : {}),
        sets: entry.sets.map((s) => {
          const resolved = resolveDefaultWeight(s.targetWeight, {
            userMax: currentMax?.valueKg,
            userMaxSource: currentMax?.source,
            userMaxSetAt: currentMax?.setAt,
            lastLogged: ctx.lastLoggedSets?.get(entry.exerciseId),
            effort: effortIntentForSet(s, isBw),
            ...(ctx.nowMs !== undefined ? { nowMs: ctx.nowMs } : {}),
          });
          // Bodyweight convention: a MAX recorded against a bodyweight
          // exercise is the TOTAL load (bw + extra), so the prescribed
          // EXTRA shown in the logger is total − bw. Only applies when
          // the chain came from the percent-of-MAX step (the rounded
          // kg is the total). 'history' / 'prescribed-absolute' values
          // ride through as-is — history is already an extra, absolute
          // is whatever the coach typed.
          let kg = resolved.kg;
          if (
            resolved.source === "computed-percent" &&
            isBw &&
            ctx.bodyweightKg !== null
          ) {
            kg = roundChainKg(kg - ctx.bodyweightKg);
          }
          return {
            reps: s.targetReps,
            weightKg: kg,
            ...(resolved.source !== "blank"
              ? { weightSource: resolved.source }
              : {}),
            ...(s.targetRpe !== undefined ? { rpe: s.targetRpe } : {}),
            ...(s.targetRir !== undefined ? { rir: s.targetRir } : {}),
            ...(s.amrap === true ? { amrap: true } : {}),
            ...(s.amrap === true && s.amrapStop ? { amrapStop: { ...s.amrapStop } } : {}),
            ...(s.repsMin !== undefined && s.repsMax !== undefined
              ? { repsMin: s.repsMin, repsMax: s.repsMax }
              : {}),
            ...(s.work !== undefined ? { work: s.work } : {}),
            ...(s.restSec !== undefined ? { restSec: s.restSec } : {}),
            ...(s.notes ? { notes: s.notes } : {}),
          };
        }),
      };
    }),
  };
};
